import { useEffect, useState } from "react";
import { Loader2, Wallet, Check, X, Ban, Upload, Eye, Landmark } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/api";
import { newRef } from "@/offline";

const rupiah = (n) => `Rp ${(n || 0).toLocaleString("id-ID")}`;
const STATUS_LABEL = {
  menunggu_verifikasi: ["MENUNGGU VERIFIKASI", "#92400e", "#fef3c7"],
  terverifikasi: ["TERVERIFIKASI", "#1e40af", "#dbeafe"],
  ditolak: ["DITOLAK", "#b91c1c", "#fee2e2"],
  dibatalkan: ["DIBATALKAN", "#475569", "#f1f5f9"],
  terposting: ["TERPOSTING", "#166534", "#dcfce7"],
};
const DepBadge = ({ s }) => {
  const [t, c, bg] = STATUS_LABEL[s] || [s, "#475569", "#f1f5f9"];
  return <span className="badge" style={{ background: bg, color: c }} data-testid={`dep-badge-${s}`}>{t}</span>;
};

// Form entry deposit — bukti TF = PERMINTAAN; deposit baru diakui setelah verifikasi + posting admin.
function DepositForm({ onSubmit, busy, testid, submitLabel }) {
  const [ref] = useState(() => newRef()); // client_ref tetap: anti double-submit/retry ganda
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("transfer");
  const [refNo, setRefNo] = useState("");
  const [note, setNote] = useState("");
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit({ client_ref: ref, amount: parseInt(amount || "0", 10), method, reference_no: refNo, note }); }}
      data-testid={`${testid}-form`} style={{ marginBottom: 12 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <div>
          <label className="field-label">Nominal (Rp)</label>
          <input className="text-input" type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} required data-testid={`${testid}-amount`} />
        </div>
        <div>
          <label className="field-label">Metode</label>
          <select className="text-input" value={method} onChange={(e) => setMethod(e.target.value)} data-testid={`${testid}-method`}>
            <option value="transfer">Transfer</option>
            <option value="cash">Cash</option>
          </select>
        </div>
      </div>
      <label className="field-label">No. Referensi (opsional)</label>
      <input className="text-input" value={refNo} onChange={(e) => setRefNo(e.target.value)} data-testid={`${testid}-refno`} />
      <label className="field-label">Catatan (opsional)</label>
      <input className="text-input" value={note} onChange={(e) => setNote(e.target.value)} data-testid={`${testid}-note`} />
      <div className="actions-right" style={{ marginTop: 8 }}>
        <button type="submit" className="btn-primary" disabled={busy} data-testid={`${testid}-submit`}>
          {busy ? <Loader2 className="spin" size={15} /> : <Wallet size={15} />} {submitLabel}
        </button>
      </div>
    </form>
  );
}

function DepositRow({ d, testid, actions }) {
  return (
    <div className="detail-box" style={{ marginBottom: 8 }} data-testid={`${testid}-row-${d.id}`}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <div>
          <strong>{rupiah(d.amount)}</strong> <span className="muted-sm">· {d.method}{d.reference_no ? ` · Ref ${d.reference_no}` : ""}</span>
          <div className="muted-sm">{new Date(d.transacted_at).toLocaleString("id-ID")}{d.note ? ` · ${d.note}` : ""}</div>
          {d.reject_reason ? <div className="muted-sm" style={{ color: "#b91c1c" }} data-testid={`${testid}-reject-${d.id}`}>Alasan tolak: {d.reject_reason}</div> : null}
        </div>
        <DepBadge s={d.status} />
      </div>
      {actions && <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>{actions}</div>}
    </div>
  );
}

// Panel admin: entry + verifikasi + posting deposit seorang reseller (digunakan di Reseller & PIN).
export function DepositAdminModal({ storeId, reseller }) {
  const q = { params: storeId ? { store_id: storeId } : {} };
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const [formKey, setFormKey] = useState(0); // remount form setelah sukses -> client_ref baru, tak mewarisi konteks transaksi sebelumnya
  const load = async () => {
    try {
      const { data } = await api.get("/admin/deposits", { ...q, params: { ...q.params, reseller_id: reseller.id } });
      setItems(data);
    } catch (err) { toast.error(errMsg(err)); }
  };
  useEffect(() => { load(); }, []);

  const act = async (id, action, body = {}) => {
    setBusy(true);
    try {
      await api.post(`/admin/deposits/${id}/${action}`, body, q);
      toast.success("Berhasil");
      await load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const create = async (body) => {
    setBusy(true);
    try {
      await api.post("/admin/deposits", { ...body, reseller_id: reseller.id }, q);
      toast.success("Deposit tercatat — MENUNGGU VERIFIKASI");
      setFormKey((k) => k + 1);
      await load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const uploadProof = async (id, file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/deposits/${id}/proof`, fd, q);
      toast.success("Bukti terunggah (status tidak berubah — tetap menunggu verifikasi)");
      await load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const viewProof = async (id) => {
    try {
      const { data } = await api.get(`/admin/deposits/${id}/proof`, { ...q, responseType: "blob" });
      window.open(URL.createObjectURL(data), "_blank");
    } catch (err) { toast.error(errMsg(err, "Bukti belum tersedia")); }
  };

  const posted = (items || []).filter((d) => d.status === "terposting");
  const totalPosted = posted.reduce((s, d) => s + d.amount, 0);
  return (
    <div data-testid="deposit-admin-panel">
      <p className="dialog-text" style={{ marginTop: 0 }}>Bukti transfer/top up adalah <strong>permintaan</strong>. Deposit hanya diakui setelah <strong>TERVERIFIKASI</strong> lalu <strong>TERPOSTING</strong> oleh admin.</p>
      <p className="muted-sm" data-testid="deposit-total-posted">Total deposit terposting: <strong>{rupiah(totalPosted)}</strong> ({posted.length} transaksi)</p>
      <DepositForm key={formKey} testid="dep-admin" busy={busy} submitLabel="Simpan Entry Deposit" onSubmit={create} />
      {items === null ? <p className="empty-note"><Loader2 className="spin" size={14} /> Memuat...</p> :
        items.length === 0 ? <p className="empty-note">Belum ada deposit.</p> :
        items.map((d) => (
          <DepositRow key={d.id} d={d} testid="dep-admin" actions={
            <>
              {d.proof_path
                ? <button className="btn-ghost sm" onClick={() => viewProof(d.id)} data-testid={`dep-proof-view-${d.id}`}><Eye size={13} /> Bukti</button>
                : <label className="btn-ghost sm" style={{ cursor: "pointer" }} data-testid={`dep-proof-up-${d.id}`}><Upload size={13} /> Bukti
                    <input type="file" accept="image/png,image/jpeg,image/webp" style={{ display: "none" }} onChange={(e) => uploadProof(d.id, e.target.files[0])} />
                  </label>}
              {d.status === "menunggu_verifikasi" && <>
                <button className="btn-primary sm" disabled={busy} onClick={() => act(d.id, "verify")} data-testid={`dep-verify-${d.id}`}><Check size={13} /> Verifikasi</button>
                <button className="btn-ghost sm" disabled={busy} onClick={() => { const r = window.prompt("Alasan penolakan:"); if (r) act(d.id, "reject", { note: r }); }} data-testid={`dep-reject-${d.id}`}><X size={13} /> Tolak</button>
                <button className="btn-ghost sm" disabled={busy} onClick={() => { const r = window.prompt("Alasan pembatalan:"); if (r !== null) act(d.id, "cancel", { note: r }); }} data-testid={`dep-cancel-${d.id}`}><Ban size={13} /> Batalkan</button>
              </>}
              {d.status === "terverifikasi" &&
                <button className="btn-primary sm" disabled={busy} onClick={() => act(d.id, "post")} data-testid={`dep-post-${d.id}`}><Landmark size={13} /> Posting Deposit</button>}
            </>
          } />
        ))}
    </div>
  );
}

// Panel mitra (login PIN di halaman toko): ajukan deposit + unggah bukti + pantau status.
export function DepositMitraPanel({ slug }) {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const load = async () => {
    try {
      const { data } = await api.get(`/public/stores/${slug}/deposits`);
      setItems(data);
    } catch { /* abaikan — panel opsional */ }
  };
  useEffect(() => { load(); }, []);
  const create = async (body) => {
    setBusy(true);
    try {
      await api.post(`/public/stores/${slug}/deposits`, body);
      toast.success("Permintaan deposit terkirim — MENUNGGU VERIFIKASI admin");
      setOpen(false); // form unmount -> client_ref baru pada pengajuan berikutnya
      await load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const uploadProof = async (id, file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/public/stores/${slug}/deposits/${id}/proof`, fd);
      toast.success("Bukti terunggah. Deposit tetap menunggu verifikasi admin.");
      await load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <section className="panel" style={{ marginBottom: 14 }} data-testid="deposit-mitra-panel">
      <div className="panel-toolbar">
        <h3 className="section-title" style={{ margin: 0 }}><Wallet size={16} /> Deposit Saya</h3>
        <button className="btn-primary sm" onClick={() => setOpen(!open)} data-testid="dep-mitra-open">{open ? "Tutup" : "Ajukan Deposit"}</button>
      </div>
      <p className="muted-sm">Bukti transfer/top up = <strong>permintaan</strong>. Deposit baru diakui setelah diverifikasi admin.</p>
      {open && <DepositForm testid="dep-mitra" busy={busy} submitLabel="Kirim Permintaan Deposit" onSubmit={create} />}
      {items && items.length > 0 && items.map((d) => (
        <DepositRow key={d.id} d={d} testid="dep-mitra" actions={
          d.status === "menunggu_verifikasi" && !d.proof_path
            ? <label className="btn-ghost sm" style={{ cursor: "pointer" }} data-testid={`dep-mitra-up-${d.id}`}><Upload size={13} /> Unggah Bukti TF
                <input type="file" accept="image/png,image/jpeg,image/webp" style={{ display: "none" }} onChange={(e) => uploadProof(d.id, e.target.files[0])} />
              </label>
            : null
        } />
      ))}
    </section>
  );
}
